import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';
import RolePresets from './roles/RolePresets';
import PermissionPreview from './roles/PermissionPreview';

const PRETTY = {
  create_project: 'Create project',
  delete_project: 'Delete project',
  edit_chapters: 'Edit chapters',
  edit_variables: 'Edit variables',
  generate_ai: 'Generate with AI',
  invite_members: 'Invite members',
  view_billing: 'View billing',
};

const ROLE_DESC = {
  admin: 'Full app — manage users, pricing, models, keys, all projects.',
  editor: 'Edit any project they belong to (chapters, characters, AI).',
  contributor: 'Suggest edits + comment; no destructive operations.',
  ordinary: 'View shared projects; create/manage their own.',
};

export default function RolesPanel() {
  const [data, setData] = useState(null);
  const [grid, setGrid] = useState({});
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await adminApi.roles();
      setData(r);
      setGrid(r.grid || {});
    } catch { toast.error('Could not load roles'); }
  };
  useEffect(() => { reload(); }, []);

  const toggle = (role, perm) => {
    setGrid((g) => ({ ...g, [role]: { ...g[role], [perm]: !g[role]?.[perm] } }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.saveRoles(grid);
      toast.success('Permissions saved.');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <section><PanelHeader title="Roles & Permissions" /><div className="mt-4 text-[12px] text-muted-foreground">Loading…</div></section>;

  const roles = data.roles || [];
  const perms = data.permissions || [];

  return (
    <section>
      <PanelHeader
        title="Roles & Permissions"
        subtitle="Toggle which capabilities each global role has. Admin can override per-project too."
        onRefresh={reload}
      />

      <div className="mt-4 overflow-x-auto rounded-md border border-border bg-card">
        <table className="w-full text-[12.5px]">
          <thead className="bg-panel-header/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Permission</th>
              {roles.map((r) => (
                <th key={r} className="px-3 py-2 text-center font-medium">
                  <div>{r}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perms.map((p) => (
              <tr key={p} className="border-t border-border">
                <td className="px-3 py-2">
                  <div className="font-medium text-foreground">{PRETTY[p] || p}</div>
                  <div className="font-mono-stat text-[10.5px] text-muted-foreground">{p}</div>
                </td>
                {roles.map((r) => (
                  <td key={r} className="px-3 py-2 text-center">
                    <Switch
                      checked={!!grid[r]?.[p]}
                      onCheckedChange={() => toggle(r, p)}
                      disabled={r === 'admin'}
                      data-testid={`role-toggle-${r}-${p}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
        {roles.map((r) => (
          <div key={r} className="rounded-md border border-border bg-panel-header/30 px-3 py-2 text-[11px] text-muted-foreground">
            <div className="text-[12px] font-semibold capitalize text-foreground">{r}</div>
            <div>{ROLE_DESC[r] || ''}</div>
          </div>
        ))}
      </div>

      <Button size="sm" onClick={save} disabled={busy} className="mt-4 gap-1.5" data-testid="roles-save">
        <Save className="h-3.5 w-3.5" /> Save permissions
      </Button>

      <RolePresets onApplied={reload} />

      <PermissionPreview />
    </section>
  );
}
